// src/components/ManageUser/ManageUserUtils.js
import { jwtDecode } from "jwt-decode";
import isAccess from "./checkAccess";
import {
  ListUserAction,
  DeleteUserAction,
  DeleteReset,
  ListUserActionFilter
} from "../Actions/AddEditUserAction";

import AccessibleTooltip from "../Component/Tooltip/Tooltip";
import EditIcon from "../assets/images/editIcon.svg";
import DeleteIcon from "../assets/images/DeleteIcon.svg";

/** Decode JWT and extract access rights */
export const getAccessRights = () => {
  const token = JSON.parse(localStorage.getItem("token"));
  const decodedToken = jwtDecode(token);
  return isAccess(decodedToken.userRole, "User Management");
};

/** Map role constants to display-friendly names */
export const handleRolesValues = (rolesValues) => {
  switch (rolesValues) {
    case "SUPER_ADMIN":
      return "Admin";
    case "MODERATOR":
      return "Viewer";
    case "BUILDING_ADMIN":
      return "Building Admin";
    case "BUILDING_MODERATOR":
      return "Building Viewer";
    default:
      return "";
  }
};

  const buildFilterPayload = (filters, page, sortConfig) => {
  const payload = {
    page: page - 1,
    size: 20,
  };

  // Search → firstName only
  if (filters.searchTerm?.trim()) {
    payload.userFullNames = [filters.searchTerm.trim()];
  }

  if (filters.role) {
    payload.customerNames = [filters.role];
  }

  if (filters.status) {
    payload.status = [filters.status];
  }

  if (sortConfig?.field) {
    payload.sortBy = sortConfig.field;
    payload.sortDirection = sortConfig.order.toUpperCase();
  }

  return payload;
};


/** Filter, sort, and format user data for table */
export const processRows = (rows) => {
  if (!Array.isArray(rows)) return [];

  return rows.map((row, index) => {
    const rawRole = row.roleName || row.userRole;

    // normalize: "Super Admin" → "SUPER_ADMIN"
    const normalizedRole = rawRole
      ? rawRole.replace(/\s+/g, "_").toUpperCase()
      : "";

    return {
      id: index + 1,
      userid: row.userGuid || "N/A",
      name: `${row.firstName} ${row.lastName}`.trim() || "N/A",
      emailaddress: row.email || "N/A",
      role: handleRolesValues(normalizedRole) || rawRole || "N/A",
      status: row.userStatus || "N/A",
      telephone: row.telephone || "N/A",
    };
  });
};


/** Fetch users list */
export const fetchUsers = (
  dispatch,
  page,
  filters = {},
  sortConfig = {}
) => {
  const hasFilter =
    Boolean(filters.searchTerm?.trim()) ||
    Boolean(filters.role) ||
    Boolean(filters.status);
  const hasSort =
    Boolean(sortConfig?.field) &&
    Boolean(sortConfig?.order);

  // Use SEARCH API if filter OR sort is applied
  if (hasFilter || hasSort) {
    dispatch(
      ListUserActionFilter({
        page: page - 1,
        size: 5,
        filters,
        sortBy: sortConfig.field || "firstName",
        sortDirection: sortConfig.order?.toUpperCase() || "ASC",
      })
    );
  } else {
    // PURE default list (old behavior)
     dispatch(
      ListUserActionFilter({
        page: page - 1,
        size: 5,
        filters,
        sortBy: sortConfig.field || "firstName",
        sortDirection: sortConfig.order?.toUpperCase() || "ASC",
      })
    );
    // dispatch(ListUserAction());
  }
};

/** Delete a specific user */
export const handleDeleteUser = (dispatch, id, createUserBtnRef) => {
  dispatch(DeleteUserAction(id));
  if (createUserBtnRef.current) {
    setTimeout(() => {
      createUserBtnRef.current.focus();
    }, 500);
  }
};

/** Reset delete snackbar */
export const resetDelete = (dispatch) => {
  dispatch(DeleteReset());
};

export const getUserColumns = ({
  edit = false,
  canDelete = false,
  handleEdit,
  setOpenDeleteModal,
  setDeviceIdToDelete,
}) => {
  const baseColumns = [
    { field: "userid", headerName: "User Id", flex: 2 },
    { field: "name", headerName: "Name", flex: 1, sortable: true },
    { field: "emailaddress", headerName: "Email Address", flex: 2, sortable: true },
    { field: "role", headerName: "Role", flex: 1 },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <span className={params.row.status}>
          {params.row.status === "ACTIVE" ? "ACTIVE" : params.row.status === "PENDING" ? "PENDING" : "INACTIVE"}
        </span>
      ),
    },
  ];

  if (edit || canDelete) {
    baseColumns.push({
      field: "Actions",
      headerName: "Actions",
      flex: 1,
      align: "right",
      headerAlign: "right",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (row) => (
        <div style={{ display: "flex", gap: "10px" }} role="list">
          {edit && (
            <AccessibleTooltip title="Edit">
              <button
                id={`edit-${row.row.id}`}
                onClick={() => handleEdit(row, `edit-${row.row.id}`)}
                className="icon-btn edit-icon-btn"
              >
                <img src={EditIcon} alt="" className="editIcon" />
              </button>
            </AccessibleTooltip>
          )}
          {canDelete && (
            <AccessibleTooltip title="Delete">
              <button
                onClick={() => {
                  setOpenDeleteModal(true);
                  setDeviceIdToDelete(row.row.userid);
                }}
                className="icon-btn delete-icon-btn"
              >
                <img src={DeleteIcon} alt="" className="deleteIcon" />
              </button>
            </AccessibleTooltip>
          )}
        </div>
      ),
    });
  }

  return baseColumns;
};
